import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AccountSwitcher from "./AccountSwitcher";
import {
  HomeIcon,
  CalendarIcon,
  MicrophoneIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  UsersIcon,
  ArrowPathIcon,
  FunnelIcon,
  ChevronLeftIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  ListBulletIcon,
  InboxIcon,
  MapPinIcon,
  TicketIcon,
  PhoneArrowUpRightIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  QueueListIcon,
  ClockIcon,
  DocumentTextIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  BookOpenIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

// Admin-only navigation (Super Admin)
const adminNavigation = [
  {
    name: "Dashboard",
    href: "/admin",
    icon: ShieldCheckIcon,
    roles: ["super_admin"],
    gradient: "from-purple-500 to-indigo-600",
  },
  {
    name: "Inquiries",
    href: "/admin/inquiries",
    icon: InboxIcon,
    roles: ["super_admin"],
    gradient: "from-orange-500 to-orange-600",
  },
  {
    name: "Organizations",
    href: "/admin/organizations",
    icon: BuildingOffice2Icon,
    roles: ["super_admin"],
    gradient: "from-teal-500 to-teal-600",
  },
  {
    name: "Locations",
    href: "/settings/locations",
    icon: MapPinIcon,
    roles: ["super_admin"],
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    name: "Logs",
    href: "/admin/logs",
    icon: MapPinIcon,
    roles: ["super_admin"],
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    name: "Manage Users",
    href: "/admin/users",
    icon: UsersIcon,
    roles: ["super_admin"],
    gradient: "from-rose-500 to-rose-600",
  },
  {
    name: "Manage Agents & TL",
    href: "/admin/agents",
    icon: PhoneArrowUpRightIcon,
    roles: ["super_admin"],
    gradient: "from-blue-500 to-blue-600",
  },
  {
    name: "Tickets",
    href: "/admin/tickets",
    icon: TicketIcon,
    roles: ["super_admin"],
    gradient: "from-amber-500 to-amber-600",
  },
  {
    name: "Manual",
    href: "/admin/manual",
    icon: BookOpenIcon,
    roles: ["super_admin"],
    gradient: "from-violet-500 to-purple-600",
  },
];

const navigation = [
  // --- Org roles ---
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: HomeIcon,
    roles: ["owner", "treatment_coordinator", "front_desk"],
    gradient: "from-brand-500 to-brand-600",
  },
  {
    name: "Appointments",
    href: "/appointments",
    icon: CalendarIcon,
    roles: ["owner", "treatment_coordinator", "front_desk"],
    gradient: "from-blue-500 to-blue-600",
  },
  {
    name: "Consultations",
    href: "/consultations",
    icon: MicrophoneIcon,
    roles: ["owner", "treatment_coordinator"],
    gradient: "from-purple-500 to-purple-600",
  },

  // Recare Workflow nav item removed — Recare Dashboard covers all functionality
  {
    name: "Recare Dashboard",
    href: "/recare-dashboard",
    icon: ChartBarIcon,
    roles: ["owner", "super_admin"],
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    name: "Treatments",
    href: "/treatments",
    icon: CurrencyDollarIcon,
    roles: ["owner"],
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    name: "Stages",
    href: "/stages",
    icon: ListBulletIcon,
    roles: ["owner"],
    gradient: "from-teal-500 to-teal-600",
  },
  {
    name: "Pipeline",
    href: "/pipeline",
    icon: FunnelIcon,
    roles: ["owner", "treatment_coordinator", "front_desk"],
    gradient: "from-cyan-500 to-cyan-600",
  },
  {
    name: "Follow-Ups",
    href: "/follow-ups",
    icon: ArrowPathIcon,
    roles: ["owner", "treatment_coordinator", "front_desk"],
    gradient: "from-amber-500 to-amber-600",
  },
  {
    name: "Call History",
    href: "/call-history",
    icon: ClockIcon,
    roles: ["front_desk"],
    gradient: "from-amber-500 to-amber-600",
  },
  {
    name: "Support",
    href: "/tickets",
    icon: TicketIcon,
    roles: ["owner", "treatment_coordinator", "front_desk"],
    gradient: "from-orange-500 to-orange-600",
  },
  {
    name: "Recycle Bin",
    href: "/recycle-bin",
    icon: TrashIcon,
    roles: ["super_admin"],
    gradient: "from-red-500 to-rose-600",
  },
  {
    name: "Manual",
    href: "/owner/manual",
    icon: BookOpenIcon,
    roles: ["owner"],
    gradient: "from-violet-500 to-purple-600",
  },
  {
    name: "Manual",
    href: "/front-desk/manual",
    icon: BookOpenIcon,
    roles: ["front_desk"],
    gradient: "from-violet-500 to-purple-600",
  },
  {
    name: "Manual",
    href: "/treatment-coordinator/manual",
    icon: BookOpenIcon,
    roles: ["treatment_coordinator"],
    gradient: "from-violet-500 to-purple-600",
  },

  // --- Agent (4 items) ---
  {
    name: "Dashboard",
    href: "/agent",
    icon: HomeIcon,
    roles: ["agent"],
    gradient: "from-blue-500 to-blue-600",
  },
  {
    name: "Lead Queue",
    href: "/agent/lead-queue",
    icon: QueueListIcon,
    roles: ["agent"],
    gradient: "from-cyan-500 to-cyan-600",
  },
  {
    name: "My Templates",
    href: "/agent/my-templates",
    icon: ChatBubbleLeftRightIcon,
    roles: ["agent"],
    gradient: "from-indigo-500 to-indigo-600",
  },
  {
    name: "Call History",
    href: "/agent/call-history",
    icon: ClockIcon,
    roles: ["agent"],
    gradient: "from-amber-500 to-amber-600",
  },
  {
    name: "My Report",
    href: "/reports",
    icon: DocumentChartBarIcon,
    roles: ["agent"],
    gradient: "from-violet-500 to-violet-600",
  },
  {
    name: "Manual",
    href: "/agent/manual",
    icon: BookOpenIcon,
    roles: ["agent"],
    gradient: "from-violet-500 to-purple-600",
  },

  // --- Team Leader (5 items) ---
  {
    name: "Dashboard",
    href: "/team-leader",
    icon: HomeIcon,
    roles: ["team_leader"],
    gradient: "from-purple-500 to-purple-600",
  },
  {
    name: "My Locations",
    href: "/team-leader/locations",
    icon: MapPinIcon,
    roles: ["team_leader"],
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    name: "My Agents",
    href: "/team-leader/agents",
    icon: UserGroupIcon,
    roles: ["team_leader"],
    gradient: "from-blue-500 to-blue-600",
  },
  {
    name: "Scripts",
    href: "/team-leader/scripts",
    icon: DocumentTextIcon,
    roles: ["team_leader"],
    gradient: "from-indigo-500 to-indigo-600",
  },
  {
    name: "Call History",
    href: "/team-leader/call-history",
    icon: ClockIcon,
    roles: ["team_leader"],
    gradient: "from-amber-500 to-amber-600",
  },
  {
    name: "Report",
    href: "/reports",
    icon: DocumentChartBarIcon,
    roles: ["team_leader", "super_admin"],
    gradient: "from-violet-500 to-violet-600",
  },
  {
    name: "Manual",
    href: "/team-leader/manual",
    icon: BookOpenIcon,
    roles: ["team_leader"],
    gradient: "from-violet-500 to-purple-600",
  },
];

const NavItem = ({ item, isActive, onClick, collapsed }) => {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={clsx(
        "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300",
        isActive ? "text-white" : "text-dark-400 hover:text-white",
        collapsed && "justify-center",
      )}
    >
      {/* Active background */}
      {isActive && (
        <div
          className={clsx(
            "absolute inset-0 rounded-xl bg-gradient-to-r opacity-100",
            item.gradient,
          )}
        />
      )}

      {/* Hover background */}
      <div
        className={clsx(
          "absolute inset-0 rounded-xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity",
          isActive && "hidden",
        )}
      />

      {/* Icon container */}
      <div
        className={clsx(
          "relative z-10 flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-300",
          isActive
            ? "bg-white/20"
            : "bg-dark-800/50 group-hover:bg-dark-700/50",
        )}
      >
        <Icon
          className={clsx(
            "w-5 h-5 transition-transform duration-300",
            !isActive && "group-hover:scale-110",
          )}
        />
      </div>

      {/* Label */}
      {!collapsed && (
        <span className="relative z-10 truncate">{item.name}</span>
      )}

      {/* Tooltip for collapsed state */}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-dark-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
          {item.name}
        </div>
      )}

      {/* Active indicator */}
      {isActive && !collapsed && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-l-full" />
      )}
    </NavLink>
  );
};

const Sidebar = ({ open, onClose, onCollapseChange }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Notify parent of collapse state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Check if super admin or agent
  const isSuperAdmin = user?.role === "super_admin";
  const isAgent = user?.role === "agent";
  const isTeamLeader = user?.role === "team_leader";

  // Filter admin and regular navigation based on user role
  const filteredAdminNav = adminNavigation.filter((item) =>
    item.roles.includes(user?.role),
  );

  // For super admin, only show regular menu if they've selected an organization
  // Agents always see their menu items (no org selection needed)
  const hasSelectedOrg = user?.selectedOrganizationId;
  const filteredNavigation = navigation.filter((item) => {
    if (!item.roles.includes(user?.role)) return false;

    if (isSuperAdmin && !hasSelectedOrg) {
      return false;
    }
    return true;
  });

  const isInSettings = location.pathname.startsWith("/settings");
  const isInAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-dark-950/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 flex flex-col",
          "bg-dark-900 border-r border-dark-800/50",
          "transform transition-all duration-300 ease-out lg:translate-x-0",
          collapsed ? "w-20" : "w-72",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 bg-mesh opacity-50 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-500/5 via-transparent to-transparent pointer-events-none" />

        {/* Logo Header */}
        <div className="relative flex items-center justify-between h-16 px-4 border-b border-dark-800/50">
          <NavLink
            to="/dashboard"
            className={clsx(
              "flex justify-center gap-3 group",
              collapsed && "justify-center w-full",
            )}
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 blur-lg opacity-50 group-hover:opacity-70 transition-opacity" />
              <img
                src="/Dpm Logo-white.png"
                alt="DPM Logo"
                className="w-10 h-10 rounded-xl object-cover"
              />
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <span className="text-lg font-bold text-white tracking-tight">
                  Chairside
                </span>
                <div className="flex items-center gap-1.5 -mt-0.5">
                  <SparklesIcon className="w-3 h-3 text-brand-400" />
                  <span className="text-[10px] text-brand-400 font-medium">
                    AI-Powered
                  </span>
                </div>
              </div>
            )}
          </NavLink>

          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg text-dark-400 hover:text-white hover:bg-dark-800 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Collapse toggle - desktop only */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={clsx(
              "hidden lg:flex items-center justify-center w-7 h-7 rounded-lg",
              "bg-dark-800 hover:bg-dark-700 text-dark-400 hover:text-white",
              "transition-all duration-300 absolute -right-3.5 top-1/2 -translate-y-1/2",
              "border border-dark-700",
            )}
          >
            <ChevronLeftIcon
              className={clsx(
                "w-4 h-4 transition-transform duration-300",
                collapsed && "rotate-180",
              )}
            />
          </button>
        </div>

        {/* User Info Card / Account Switcher */}
        <div className="relative mx-3 mt-4">
          <AccountSwitcher collapsed={collapsed} />
        </div>

        {/* Navigation */}
        <nav
          className={clsx(
            "flex-1 px-3 py-2 space-y-1 overflow-y-auto scrollbar-hide",
            collapsed ? "mt-4" : "mt-6",
          )}
        >
          {/* Admin Navigation (Super Admin Only) */}
          {filteredAdminNav.length > 0 && (
            <>
              {!collapsed && (
                <div className="px-2 mb-2">
                  <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-widest flex items-center gap-1">
                    <ShieldCheckIcon className="w-3 h-3" />
                    Admin
                  </p>
                </div>
              )}
              {filteredAdminNav.map((item) => (
                <NavItem
                  key={item.name}
                  item={item}
                  isActive={location.pathname === item.href}
                  onClick={onClose}
                  collapsed={collapsed}
                />
              ))}
              <div
                className={clsx(
                  "my-3",
                  !collapsed && "mx-2 border-t border-dark-700/50",
                )}
              />
            </>
          )}

          {/* Regular Navigation */}
          {filteredNavigation.length > 0 && (
            <>
              {!collapsed && (
                <div className="px-2 mb-2">
                  <p className="text-[10px] font-semibold text-dark-500 uppercase tracking-widest">
                    {isSuperAdmin && hasSelectedOrg
                      ? "Organization Menu"
                      : "Menu"}
                  </p>
                </div>
              )}
              {filteredNavigation.map((item) => (
                <NavItem
                  key={item.href}
                  item={item}
                  isActive={location.pathname === item.href}
                  onClick={onClose}
                  collapsed={collapsed}
                />
              ))}
            </>
          )}

          {/* Super Admin: No org selected message */}
          {isSuperAdmin && !hasSelectedOrg && !collapsed && (
            <div className="px-3 py-4 mx-2 mt-2 rounded-xl bg-dark-800/50 border border-dark-700/50">
              <p className="text-xs text-dark-400 text-center">
                Select an organization from the header to view
                organization-specific features
              </p>
            </div>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="relative px-3 py-3 border-t border-dark-800/50 space-y-1">
          {/* Settings — visible to owner and super_admin only */}
          {user?.role === "owner" && (
            <NavLink
              to="/settings"
              onClick={onClose}
              className={clsx(
                "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300",
                isInSettings ? "text-white" : "text-dark-400 hover:text-white",
                collapsed && "justify-center",
              )}
            >
              {isInSettings && (
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-dark-600 to-dark-700" />
              )}
              <div
                className={clsx(
                  "absolute inset-0 rounded-xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity",
                  isInSettings && "hidden",
                )}
              />

              <div
                className={clsx(
                  "relative z-10 flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-300",
                  isInSettings
                    ? "bg-white/20"
                    : "bg-dark-800/50 group-hover:bg-dark-700/50",
                )}
              >
                <Cog6ToothIcon className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
              </div>
              {!collapsed && <span className="relative z-10">Settings</span>}

              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-dark-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  Settings
                </div>
              )}
            </NavLink>
          )}

          {/* Help Assistant */}
          <button
            type="button"
            onClick={() => {
              onClose?.();
              window.dispatchEvent(new Event("helpAssistant:open"));
            }}
            className={clsx(
              "group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
              "text-dark-400 hover:text-white transition-all duration-300",
              collapsed && "justify-center",
            )}
          >
            <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/5 transition-colors" />
            <div className="relative z-10 flex items-center justify-center w-9 h-9 rounded-lg bg-dark-800/50 group-hover:bg-dark-700/50 transition-all duration-300">
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
            </div>
            {!collapsed && <span className="relative z-10">Help</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-dark-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                Help
              </div>
            )}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={clsx(
              "group relative w-full text-error-400  flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
              "text-dark-400 hover:text-error-400 transition-all duration-300",
              collapsed && "justify-center",
            )}
          >
            <div className="absolute inset-0 rounded-xl bg-error-500/0 group-hover:bg-error-500/10 transition-colors" />

            <div className="relative z-10 flex items-center justify-center w-9 h-9 rounded-lg bg-dark-800/50 group-hover:bg-error-500/20 transition-all duration-300">
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </div>
            {!collapsed && <span className="relative z-10">Logout</span>}

            {/* Tooltip for collapsed state */}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-dark-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                Logout
              </div>
            )}
          </button>
        </div>

        {/* Version Footer */}
        {!collapsed && (
          <div className="relative px-5 py-3 border-t border-dark-800/50">
            <div className="flex items-center justify-between">
              <p className="text-xs text-dark-500">Chairside v1.0</p>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-500/10 border border-success-500/20">
                <div className="w-1.5 h-1.5 rounded-full bg-success-500 animate-pulse" />
                <span className="text-[10px] text-success-400 font-medium">
                  Online
                </span>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
